import { useState, useEffect } from "react";
import SectionBg from "../components/SectionBg";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import { toast } from "sonner";
import { Play, Plus, Save, X, Edit3, Trash2, ArrowLeft, ExternalLink, ChevronRight } from "lucide-react";

const convertToEmbedUrl = (url) => {
  if (!url) return "";
  if (url.includes("/embed/") || url.includes("/preview")) return url;
  const driveMatch = url.match(/\/d\/([^\/]+)/);
  if (driveMatch) return `https://drive.google.com/file/d/${driveMatch[1]}/preview`;
  const driveIdMatch = url.match(/[?&]id=([^&]+)/);
  if (driveIdMatch) return `https://drive.google.com/file/d/${driveIdMatch[1]}/preview`;
  let videoId = null;
  const watchMatch = url.match(/[?&]v=([^&]+)/);
  if (watchMatch) videoId = watchMatch[1];
  const shortMatch = url.match(/youtu\.be\/([^?&]+)/);
  if (shortMatch) videoId = shortMatch[1];
  const shortsMatch = url.match(/shorts\/([^?&]+)/);
  if (shortsMatch) videoId = shortsMatch[1];
  if (videoId) return `https://www.youtube.com/embed/${videoId}`;
  return url;
};

export default function SubCategoriaVideos() {
  const { slug, subslug } = useParams();
  const { isAdmin } = useAuth();
  const [category, setCategory] = useState(null);
  const [subcategory, setSubcategory] = useState(null);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ title: "", content: "" });
  const [showAddVideo, setShowAddVideo] = useState(false);
  const [newVideo, setNewVideo] = useState({ title: "", url: "" });

  useEffect(() => { fetchContent(); }, [slug, subslug]);

  const fetchContent = async () => {
    try {
      const [slotsRes, subsRes, videosRes] = await Promise.all([
        axios.get(`${API}/content/servizi-slots`),
        axios.get(`${API}/content/servizi-subcategories`),
        axios.get(`${API}/content/servizi-videos`)
      ]);
      setCategory(slotsRes.data.find(s => s.content === slug || (() => { try { return JSON.parse(s.content).slug === slug; } catch { return false; } })()));

      const allSubs = subsRes.data.map(s => { try { return { ...s, ...JSON.parse(s.content) }; } catch { return s; } });
      setSubcategory(allSubs.find(s => s.slug === subslug && s.parent_slug === slug));

      setVideos(videosRes.data.filter(v => {
        try {
          const parsed = JSON.parse(v.content);
          return parsed.slot === slug && parsed.subcategory === subslug;
        } catch { return false; }
      }));
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const getVideoUrl = (video) => {
    try { return JSON.parse(video.content).url; }
    catch { return convertToEmbedUrl(video.content); }
  };

  const addVideo = async () => {
    if (!newVideo.url.trim()) { toast.error("Inserisci un URL video"); return; }
    try {
      await axios.post(`${API}/content`, {
        section: "servizi-videos",
        content_type: "video",
        title: newVideo.title || "Nuovo Video",
        content: JSON.stringify({ url: convertToEmbedUrl(newVideo.url), slot: slug, subcategory: subslug }),
        order: videos.length
      });
      toast.success("Video aggiunto");
      setNewVideo({ title: "", url: "" });
      setShowAddVideo(false);
      fetchContent();
    } catch { toast.error("Errore"); }
  };

  const saveVideoEdit = async (id) => {
    try {
      await axios.put(`${API}/content/${id}`, {
        title: editForm.title,
        content: JSON.stringify({ url: convertToEmbedUrl(editForm.content), slot: slug, subcategory: subslug })
      });
      toast.success("Aggiornato");
      setEditingId(null);
      fetchContent();
    } catch { toast.error("Errore"); }
  };

  const deleteVideo = async (id) => {
    try {
      await axios.delete(`${API}/content/${id}`);
      toast.success("Eliminato");
      fetchContent();
    } catch { toast.error("Errore"); }
  };

  if (loading) {
    return (
      <div className="page-container flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-t-transparent rounded-full" style={{ borderColor: "var(--mm-gold)", borderTopColor: "transparent" }} />
      </div>
    );
  }

  return (
    <div className="page-container trading-bg" data-testid="subcategoria-videos-page">
      <SectionBg type="trading" />
      <div className="container py-8 sm:py-12 relative z-10">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 mb-6 text-sm flex-wrap" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          <Link to="/servizi" className="hover:underline" style={{ color: "var(--mm-gold)" }}>Servizi</Link>
          <ChevronRight size={14} style={{ color: "var(--mm-text-muted)" }} />
          <Link to={`/servizi/${slug}`} className="hover:underline" style={{ color: "var(--mm-gold)" }}>{category?.title || slug}</Link>
          <ChevronRight size={14} style={{ color: "var(--mm-text-muted)" }} />
          <span style={{ color: "var(--mm-text-primary)" }}>{subcategory?.title || subslug}</span>
        </div>

        {/* Header */}
        <div className="text-center mb-10">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 gold-text" style={{ fontFamily: "'Space Grotesk', sans-serif" }} data-testid="subcategory-title">
            {subcategory?.title || subslug}
          </h1>
          <div className="w-24 h-1 mx-auto" style={{ backgroundColor: "var(--mm-gold)" }} />
        </div>

        {/* Admin Add Video */}
        {isAdmin && (
          <div className="mb-8">
            {!showAddVideo ? (
              <button onClick={() => setShowAddVideo(true)} className="btn-primary flex items-center gap-2 mx-auto" data-testid="add-video-btn">
                <Plus size={18} /> Aggiungi Video
              </button>
            ) : (
              <div className="glass-card p-5 max-w-lg mx-auto space-y-4">
                <h3 className="text-lg font-semibold" style={{ color: "var(--mm-gold)", fontFamily: "'Space Grotesk', sans-serif" }}>Nuovo Video</h3>
                <input type="text" value={newVideo.title} onChange={(e) => setNewVideo({ ...newVideo, title: e.target.value })} className="admin-input" placeholder="Titolo video" />
                <input type="text" value={newVideo.url} onChange={(e) => setNewVideo({ ...newVideo, url: e.target.value })} className="admin-input" placeholder="Link YouTube o Google Drive" />
                <div className="flex gap-3">
                  <button onClick={addVideo} className="btn-primary flex-1 flex items-center justify-center gap-2"><Save size={16} /> Salva</button>
                  <button onClick={() => { setShowAddVideo(false); setNewVideo({ title: "", url: "" }); }} className="btn-secondary flex-1">Annulla</button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Videos Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {videos.map((video, index) => (
            <div key={video.id} className="video-card" data-testid={`video-${index}`}>
              {editingId === video.id ? (
                <div className="p-4 space-y-3">
                  <input type="text" value={editForm.title} onChange={(e) => setEditForm({ ...editForm, title: e.target.value })} className="admin-input" placeholder="Titolo" />
                  <input type="text" value={editForm.content} onChange={(e) => setEditForm({ ...editForm, content: e.target.value })} className="admin-input" placeholder="Link video" />
                  <div className="flex gap-2">
                    <button onClick={() => saveVideoEdit(video.id)} className="btn-primary flex-1 py-2 text-sm">Salva</button>
                    <button onClick={() => setEditingId(null)} className="btn-secondary flex-1 py-2 text-sm">Annulla</button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="video-container">
                    <iframe src={getVideoUrl(video)} title={video.title} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen" allowFullScreen frameBorder="0" />
                  </div>
                  <div className="p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: "rgba(47, 216, 168, 0.2)" }}>
                        <Play size={16} style={{ color: "var(--mm-gold)" }} />
                      </div>
                      <h3 className="text-base sm:text-lg font-semibold truncate" style={{ fontFamily: "'Space Grotesk', sans-serif", color: "var(--mm-text-primary)" }}>{video.title}</h3>
                    </div>
                    {isAdmin && (
                      <div className="flex gap-1 flex-shrink-0">
                        <button onClick={() => { setEditingId(video.id); try { setEditForm({ title: video.title, content: JSON.parse(video.content).url }); } catch { setEditForm({ title: video.title, content: video.content }); } }} className="p-2 rounded-lg" style={{ backgroundColor: "rgba(47, 216, 168, 0.1)" }}>
                          <Edit3 size={16} style={{ color: "var(--mm-gold)" }} />
                        </button>
                        <button onClick={() => deleteVideo(video.id)} className="p-2 rounded-lg" style={{ backgroundColor: "rgba(239, 68, 68, 0.1)" }}>
                          <Trash2 size={16} style={{ color: "#EF4444" }} />
                        </button>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          ))}
        </div>

        {videos.length === 0 && (
          <div className="glass-card p-12 text-center max-w-md mx-auto">
            <Play size={48} style={{ color: "var(--mm-gold)", margin: "0 auto 16px" }} />
            <p style={{ color: "var(--mm-text-secondary)" }}>{isAdmin ? "Aggiungi il primo video" : "I video verranno aggiunti a breve"}</p>
          </div>
        )}
      </div>
    </div>
  );
}
